import useClientPagination from 'hooks/use-client-pagination'
import UrlPagination from 'components/molecules/url-pagination'
import BannersApiV2 from 'api/v2/admins/banners-api-v2'
import ArchiveFilter from 'domains/setting/components/archive-filter'
import { errorMessage } from 'domains/dashboard-migration/api'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table
} from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const emptyValues = {
  name           : '',
  is_live        : false,
  url            : '',
  sort_order     : '',
  image          : null,
  vertical_image : null
}

const BannerPage = ({ pageUtils }) => {
  const [ banners, setBanners ] = useState([])
  const [ query, setQuery ] = useState('')
  const [ archived, setArchived ] = useState(false)
  const [ error, setError ] = useState('')
  const [ isArchiving, setIsArchiving ] = useState(false)
  const archiveOperation = useRef(false)
  const fetchSequence = useRef(0)
  const showError = useCallback(async (err) => setError(await errorMessage(err)), [])
  const changeValue = (name, value) => setValues((current) => ({ ...current, [name]: value }))
  const [ selected, setSelected ] = useState(null)
  const [ values, setValues ] = useState(emptyValues)
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSaving, setIsSaving ] = useState(false)

  const fetchBanners = useCallback(() => {
    const sequence = ++fetchSequence.current
    setError('')
    setIsLoading(true)
    BannersApiV2.get(archived)
      .then((response) => { if (sequence === fetchSequence.current) setBanners(response.data.banners || []) })
      .catch(showError)
      .finally(() => { if (sequence === fetchSequence.current) setIsLoading(false) })
  }, [ archived, showError ])

  useEffect(() => {
    fetchBanners()
  }, [ fetchBanners ])

  const filteredBanners = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword) return banners
    return banners.filter((banner) => [ banner.name, banner.url, banner.sort_order ].some((value) => String(value ?? '').toLowerCase().includes(keyword)))
  }, [ banners, query ])

  const closeModal = () => {
    setIsModalOpen(false)
    setSelected(null)
    setValues(emptyValues)
  }

  const openCreate = () => {
    setSelected(null)
    setValues(emptyValues)
    setIsModalOpen(true)
  }

  const openEdit = (banner) => {
    setSelected(banner)
    setValues({
      name           : banner.name || '',
      is_live        : Boolean(banner.is_live),
      url            : banner.url || '',
      sort_order     : banner.sort_order ?? '',
      image          : null,
      vertical_image : null
    })
    setIsModalOpen(true)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setIsSaving(true)
    const request = selected ? BannersApiV2.update(selected.id, values) : BannersApiV2.create(values)

    request
      .then(() => {
        pageUtils?.setAlertMsg?.(`Banner has been ${selected ? 'updated' : 'created'}`, ALERT_TYPES.SUCCESS)
        closeModal()
        fetchBanners()
      })
      .catch(showError)
      .finally(() => setIsSaving(false))
  }

  const handleDelete = (banner) => {
    if (!window.confirm(`Archive banner ${banner.name || `#${banner.id}`}? Data dan gambar tetap disimpan.`)) return

    if (archiveOperation.current) return
    archiveOperation.current = true
    setIsArchiving(true)
    BannersApiV2.destroy(banner.id)
      .then(() => {
        pageUtils?.setAlertMsg?.('Banner has been archived', ALERT_TYPES.SUCCESS)
        fetchBanners()
      })
      .catch(showError)
      .finally(() => { archiveOperation.current = false; setIsArchiving(false) })
  }

  const handleRestore = (banner) => {
    if (archiveOperation.current) return
    if (!window.confirm(`Restore banner ${banner.name || `#${banner.id}`}?`)) return
    archiveOperation.current = true
    setIsArchiving(true)
    BannersApiV2.restore(banner.id)
      .then(() => { pageUtils?.setAlertMsg?.('Banner has been restored', ALERT_TYPES.SUCCESS); fetchBanners() })
      .catch(showError)
      .finally(() => { archiveOperation.current = false; setIsArchiving(false) })
  }

  const listPagination = useClientPagination(filteredBanners, { loading: isLoading })

  return (
    <>
      <TitlePage mainTitle='Setting' subTitle='Banners' />
      { error && !isModalOpen && <div role='alert' className='alert alert-danger'>{ error }</div> }
      <Card>
        <CardBody>
          <Row className='mb-3 align-items-center'>
            <Col md={ 5 }>
              <Input value={ query } onChange={ (event) => { listPagination.setPage(1); setQuery(event.target.value) } } placeholder='Search banners' />
            </Col>
            <Col md={ 7 } className='text-md-right mt-2 mt-md-0'>
              { !archived && <Button color='primary' onClick={ openCreate }>{ 'Create Banner' }</Button> }
            </Col>
          </Row>

          <ArchiveFilter value={ archived } change={ (value) => { listPagination.setPage(1); setArchived(value) } } disabled={ isArchiving } />
          <Table hover responsive>
            <thead>
              <tr>
                <th>{ 'Name' }</th><th>{ 'Live' }</th><th>{ 'Clicked URL' }</th><th>{ 'Sort Order' }</th><th>{ 'Image' }</th><th>{ 'Vertical Image' }</th><th>{ 'Action' }</th>
              </tr>
            </thead>
            <tbody>
              { !isLoading && listPagination.rows.map((banner) => (
                <tr key={ banner.id }>
                  <td>{ banner.name || '-' }</td>
                  <td>{ banner.is_live ? <Badge color='success'>{ 'Yes' }</Badge> : <Badge color='secondary'>{ 'No' }</Badge> }</td>
                  <td>{ banner.url || '-' }</td>
                  <td>{ banner.sort_order ?? '-' }</td>
                  <td>{ banner.image_url ? <a href={ banner.image_url } target='_blank' rel='noopener noreferrer'>{ 'View' }</a> : '-' }</td>
                  <td>{ banner.vertical_image_url ? <a href={ banner.vertical_image_url } target='_blank' rel='noopener noreferrer'>{ 'View' }</a> : '-' }</td>
                  <td className='text-nowrap'>
                    { !banner.deleted_at && <Button color='info' size='sm' className='mr-2' onClick={ () => openEdit(banner) }>{ 'Edit' }</Button> }
                    <Button color={ banner.deleted_at ? 'success' : 'warning' } size='sm' disabled={ isArchiving } onClick={ () => banner.deleted_at ? handleRestore(banner) : handleDelete(banner) }>{ banner.deleted_at ? 'Restore' : 'Archive' }</Button>
                  </td>
                </tr>
              )) }
              { !isLoading && filteredBanners.length === 0 && <tr><td colSpan='7' className='text-center text-muted'>{ 'No data' }</td></tr> }
              { isLoading && <tr><td colSpan='7' className='text-center'>{ 'Loading...' }</td></tr> }
            </tbody>
          </Table>
          <UrlPagination pagination={ listPagination } loading={ isLoading } />
        </CardBody>
      </Card>

      <Modal isOpen={ isModalOpen } toggle={ closeModal }>
        <form onSubmit={ handleSubmit }>
          <ModalHeader toggle={ closeModal }>{ `${selected ? 'Edit' : 'Create'} Banner` }</ModalHeader>
          <ModalBody>
            { error && <div role='alert' className='alert alert-danger'>{ error }</div> }
            <FormGroup><Label>{ 'Name' }</Label><Input value={ values.name } onChange={ (event) => changeValue('name', event.target.value) } /></FormGroup>
            <FormGroup check className='mb-3'><Label check><Input type='checkbox' checked={ values.is_live } onChange={ (event) => changeValue('is_live', event.target.checked) } />{ ' Live' }</Label></FormGroup>
            <FormGroup><Label>{ 'Clicked URL' }</Label><Input value={ values.url } onChange={ (event) => changeValue('url', event.target.value) } /></FormGroup>
            <FormGroup><Label>{ 'Sort Order' }</Label><Input type='number' value={ values.sort_order } onChange={ (event) => changeValue('sort_order', event.target.value) } /></FormGroup>
            <FormGroup>
              <Label>{ 'Horizontal Image' }</Label>
              <Input type='file' accept='image/*' onChange={ (event) => changeValue('image', event.target.files?.[0] || null) } />
              <small className='text-muted'>{ 'Trestle recommendation: 3840x1800.' }</small>
              { selected?.image_url && <div className='mt-1'><a href={ selected.image_url } target='_blank' rel='noopener noreferrer'>{ 'View current image' }</a></div> }
            </FormGroup>
            <FormGroup>
              <Label>{ 'Vertical Image' }</Label>
              <Input type='file' accept='image/*' onChange={ (event) => changeValue('vertical_image', event.target.files?.[0] || null) } />
              <small className='text-muted'>{ 'Trestle recommendation: 800x1200.' }</small>
              { selected?.vertical_image_url && <div className='mt-1'><a href={ selected.vertical_image_url } target='_blank' rel='noopener noreferrer'>{ 'View current image' }</a></div> }
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type='button' color='secondary' onClick={ closeModal } disabled={ isSaving }>{ 'Cancel' }</Button>
            <Button type='submit' color='primary' disabled={ isSaving }>{ isSaving ? 'Saving...' : 'Save' }</Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  )
}

BannerPage.propTypes = propTypes
export default BannerPage
